<script setup lang="ts">
import { ref } from "vue";
import { useTranslation } from "i18next-vue";
import { useApplyFieldPartTemplate } from "@/stores/queries/field-observations.query";
import FieldPartTemplatePicker from "@/components/field-part-templates/FieldPartTemplatePicker.vue";

/**
 * Insertion d'un modèle de parties — feuille du bas, pour la saisie terrain au
 * doigt. La variante « outil dessin » est une vraie modal :
 * `InsertPartTemplateDialog.vue`. Les deux partagent `FieldPartTemplatePicker`.
 */

const props = defineProps<{
  modelValue: boolean;
  diagnosticId: number;
  parentPartId?: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "inserted"): void;
}>();

const { t } = useTranslation();

const { mutate: applyTemplate, isPending } = useApplyFieldPartTemplate();

const selectedTemplateId = ref<number | null>(null);

const close = () => {
  selectedTemplateId.value = null;
  emit("update:modelValue", false);
};

const handleInsert = () => {
  if (!selectedTemplateId.value) return;

  applyTemplate(
    {
      diagnosticId: props.diagnosticId,
      templateId: selectedTemplateId.value,
      parentPartId: props.parentPartId,
    },
    {
      onSuccess: () => {
        emit("inserted");
        close();
      },
    },
  );
};
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="popup-overlay" @click.self="close">
      <div class="popup-content">
        <h4>
          {{ t("dashboard:fieldObservations.partTemplates.insertModal.title") }}
        </h4>

        <FieldPartTemplatePicker v-model="selectedTemplateId" />

        <div class="popup-actions">
          <button class="popup-btn cancel" @click="close">
            {{ t("cancel") }}
          </button>
          <button
            class="popup-btn confirm"
            :disabled="!selectedTemplateId || isPending"
            @click="handleInsert"
          >
            <span
              v-if="isPending"
              class="spinner-border spinner-border-sm me-2"
            />
            {{
              t("dashboard:fieldObservations.partTemplates.insertModal.insert")
            }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
}

.popup-content {
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  background-color: var(--bs-white);
  border-radius: 20px 20px 0 0;
  padding: 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));

  h4 {
    margin: 0 0 16px;
    font-size: 18px;
    font-weight: 600;
  }
}

.popup-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.popup-btn {
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &.cancel {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-body-color);
  }

  &.confirm {
    background-color: var(--bs-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
